import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { IconCalendar, IconCheckbox, IconSend } from "@tabler/icons-react"
import { useMemo } from "react"
import { speakerTextColor } from "../../../../../lib/speaker-colors"
import { speakerVMFromName } from "../../../../../lib/speaker-vm"
import { EditableAnalysisField } from "../../../../EditableAnalysisField"
import { EditableField } from "../../../../EditableField"
import { SpeakerChip } from "../../../../SpeakerChip"
import { resolveSeekTime } from "../../../helpers"
import { SeekControl } from "../../../parts/SeekControl"
import classes from "../../../styles.module.css"
import type { IViewProps } from "./interfaces"

const ACTION_GROUPS: { key: "high" | "med" | "low"; tone: "high" | "med" | "low" }[] = [
  { key: "high", tone: "high" },
  { key: "med", tone: "med" },
  { key: "low", tone: "low" },
]

/**
 * ActionItemsCardView groups action items by priority (high / med / low). There
 * is deliberately no done-tracking here: items are tracked in the external
 * system they get exported to (Jira et al.), not in the meeting notes — each row
 * carries a hover-revealed "To Jira" export affordance instead (always visible
 * on touch devices, where hover doesn't exist).
 *
 * Dumb view: props in, events out. The owner edit fires `onOwnerSave`; the smart
 * wrapper (../index.tsx) owns the company API + persistence.
 *
 * Owner avatars derive their colour from `resolveSpeakerColor()` — same palette
 * as the player legend, so an owner's chip stays visually linked to their
 * waveform band.
 */
export function ActionItemsCardView({
  items,
  speakerColorMap,
  onExportItem,
  segments,
  seekOffsets,
  editContext,
  onOwnerSave,
}: IViewProps): React.JSX.Element | null {
  const { i18n } = useLingui()
  const seekTimes = useMemo(
    () => items.map((it, i) => resolveSeekTime(seekOffsets?.[i], it.description, segments)),
    [items, segments, seekOffsets],
  )

  if (!items || items.length === 0) return null

  return (
    <section className={classes.card}>
      <div className={classes.cardHead}>
        <h2 className={classes.cardTitle}>
          <IconCheckbox size={16} />
          <Trans>Action items</Trans>
        </h2>
        <span className={classes.cardCount}>{items.length}</span>
      </div>

      <div className={classes.actionsGroups}>
        {ACTION_GROUPS.map((group) => {
          const groupItems = items
            .map((item, idx) => ({ item, idx }))
            .filter(({ item }) => (item.priority ?? "med") === group.key)
          if (groupItems.length === 0) return null
          return (
            <div key={group.key}>
              <div className={classes.agroupHead} data-tone={group.tone}>
                <span className={classes.agroupDot} />
                <span>
                  {group.tone === "high" && <Trans>High priority</Trans>}
                  {group.tone === "med" && <Trans>Medium</Trans>}
                  {group.tone === "low" && <Trans>Low</Trans>}
                </span>
                <span className={classes.agroupCount}>{groupItems.length}</span>
                <span className={classes.agroupRule} />
              </div>
              <ul className={classes.actionsList}>
                {groupItems.map(({ item, idx }) => {
                  const ownerTextColor = speakerTextColor(item.owner, speakerColorMap)
                  const descPath = `action_points.${idx}.description`
                  const ownerPath = `action_points.${idx}.owner`
                  const dueDatePath = `action_points.${idx}.deadline`
                  return (
                    <li key={idx} className={classes.actionRow}>
                      <div className={classes.actionBody}>
                        <p className={classes.actionText}>
                          {editContext ? (
                            <EditableAnalysisField
                              meetingId={editContext.meetingId}
                              fieldPath={descPath}
                              value={item.description}
                              canEdit={editContext.canEdit}
                              isEdited={!!editContext.fieldEdits[descPath]}
                              onSaved={editContext.onEdited}
                            />
                          ) : (
                            item.description
                          )}
                        </p>
                        <div className={classes.actionSub}>
                          {item.owner && (
                            <span className={classes.actionOwner} style={{ color: ownerTextColor }}>
                              <SpeakerChip
                                variant="avatar"
                                size="xs"
                                speaker={speakerVMFromName(item.owner, speakerColorMap)}
                              />
                              {editContext?.canEdit ? (
                                <EditableField
                                  value={item.owner}
                                  onSave={(v) => onOwnerSave(ownerPath, v)}
                                  size="xs"
                                  c="var(--lex-text-info)"
                                  underline="dotted"
                                  width={120}
                                />
                              ) : (
                                <span>{item.owner}</span>
                              )}
                            </span>
                          )}
                          {item.owner && item.deadline && <span className={`${classes.actionDot} lex-dot`} />}
                          {item.deadline && (
                            <span className={classes.actionDue}>
                              <IconCalendar size={12} />
                              {editContext ? (
                                <EditableAnalysisField
                                  meetingId={editContext.meetingId}
                                  fieldPath={dueDatePath}
                                  value={item.deadline}
                                  canEdit={editContext.canEdit}
                                  isEdited={!!editContext.fieldEdits[dueDatePath]}
                                  onSaved={editContext.onEdited}
                                />
                              ) : (
                                <span>{item.deadline}</span>
                              )}
                            </span>
                          )}
                          {/* No separator before the timestamp: SeekControl
                              self-suppresses when the player is unavailable,
                              so the card can't know whether it renders. */}
                          <SeekControl seekTime={seekTimes[idx]} />
                        </div>
                      </div>
                      <button
                        type="button"
                        className={classes.actionExport}
                        onClick={() => onExportItem(idx)}
                        title={i18n._(msg`Sync to Jira (coming soon)`)}
                      >
                        <IconSend size={12} />
                        <Trans>To Jira</Trans>
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          )
        })}
      </div>
    </section>
  )
}
